import { Menu, LogOut, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchableSelector } from "@/components/searchable-selector";
import { useAuth } from "@/auth/auth-provider";
import { initialsFromName } from "@/lib/format";

export function Topbar({ onMenuClick }: { onMenuClick: () => void }): JSX.Element {
  const { me, previewMode, previewUsers, selectedPreviewSubject, setPreviewSubject, setOrganization, logout } =
    useAuth();
  const navigate = useNavigate();
  const switchOrganization = (organizationId: number): void => {
    if (organizationId === me?.tenant_id ||
        !me?.organizations.some((organization) => organization.tenant_id === organizationId)) return;
    // URLs carry analytics filters and controller identities; neither belongs
    // to the next organization's workspace.
    navigate("/dashboard", { replace: true });
    setOrganization(organizationId);
  };

  return (
    <header className="flex h-16 min-w-0 shrink-0 items-center gap-3 border-b border-border bg-card px-4 sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenuClick}
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold leading-none">ESUMEI Cloud</span>
        <span className="truncate text-xs text-muted-foreground">
          {me?.organizations.find((organization) => organization.tenant_id === me.tenant_id)?.name ??
            (me?.tenant_id ? `Organization #${me.tenant_id}` : "All organizations")} · {me?.roles.join(", ")}
        </span>
      </div>

      <div className="ml-auto flex min-w-0 shrink-0 items-center gap-3">
        {!previewMode && (me?.organizations.length ?? 0) > 1 ? (
          <div className="w-[120px] sm:w-[160px]">
          <SearchableSelector label="Organization" value={String(me?.tenant_id ?? "")}
            onChange={(value) => switchOrganization(Number(value))}
            options={(me?.organizations ?? []).map((organization) => ({
              value: String(organization.tenant_id), label: organization.name ?? `Organization #${organization.tenant_id}`,
            }))} />
          </div>
        ) : null}
        {previewMode && previewUsers.length > 0 ? (
          <div className="hidden items-center gap-2 sm:flex">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Acting as
            </span>
            <div className="w-[220px]">
            <SearchableSelector label="Operator" placeholder="Select operator"
              value={selectedPreviewSubject}
              options={previewUsers.map((user) => ({ value: user.identity_subject, label: `${user.display_name} · ${user.role}` }))}
              onChange={(value) => {
                navigate("/dashboard", { replace: true });
                setPreviewSubject(value);
              }}
            />
            </div>
          </div>
        ) : null}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-10 gap-2 px-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback>{initialsFromName(me?.display_name)}</AvatarFallback>
              </Avatar>
              <div className="hidden min-w-0 max-w-48 text-left lg:block">
                <div className="truncate text-sm font-semibold leading-none">
                  {me?.display_name ?? "Operator"}
                </div>
                <div className="truncate text-xs text-muted-foreground">{me?.email}</div>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Signed in</DropdownMenuLabel>
            <DropdownMenuItem disabled>
              <User className="h-4 w-4" />
              <span className="truncate">{me?.email ?? "anonymous"}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {!previewMode ? (
              <DropdownMenuItem onClick={() => logout()}>
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled>
                <LogOut className="h-4 w-4" />
                Local access mode
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
